import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

/* ================== ENV ================== */
const GITHUB_USERNAME = process.env.REACT_APP_GITHUB_USERNAME;
const REPO_NAME = process.env.REACT_APP_GITHUB_REPO;
const BRANCH = process.env.REACT_APP_GITHUB_BRANCH;
const GITHUB_TOKEN = process.env.REACT_APP_GITHUB_TOKEN;

/* ================== AXIOS ================== */
const githubApi = axios.create({
  baseURL: "https://api.github.com",
  headers: {
    Authorization: `Bearer ${GITHUB_TOKEN}`,
    Accept: "application/vnd.github+json",
  },
});

/* ================== SKELETON ================== */
const Skeleton = () => (
  <div className="w-full h-full rounded-2xl bg-white/10 animate-pulse" />
);

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  /* 🔥 LIGHTBOX STATE */
  const [lightbox, setLightbox] = useState({
    open: false,
    index: 0,
  });

  /* ================== FETCH GALLERY ================== */
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const cached = sessionStorage.getItem("gallery_images");
        if (cached) {
          setImages(JSON.parse(cached));
          setLoading(false);
          return;
        }

        const res = await githubApi.get(
          `/repos/${GITHUB_USERNAME}/${REPO_NAME}/contents/gallery`
        );

        const imgs = res.data
          .filter(item => item.type === "file")
          .map(item => ({
            src: `https://raw.githubusercontent.com/${GITHUB_USERNAME}/${REPO_NAME}/${BRANCH}/gallery/${item.name}`,
            alt: item.name.replace(/[-_]/g, " ").split(".")[0],
          }));

        setImages(imgs);
        sessionStorage.setItem("gallery_images", JSON.stringify(imgs));
      } catch (err) {
        console.error("Failed to load gallery images", err);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  /* 🔐 KEYBOARD CONTROLS */
  useEffect(() => {
    if (!lightbox.open) return;

    const handleKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightbox.open, lightbox.index, images]);

  /* ================== LIGHTBOX ACTIONS ================== */
  const openLightbox = (index) => {
    setLightbox({ open: true, index });
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightbox({ open: false, index: 0 });
    document.body.style.overflow = "";
  };

  const nextImage = () => {
    setLightbox((s) => ({
      ...s,
      index: (s.index + 1) % images.length,
    }));
  };

  const prevImage = () => {
    setLightbox((s) => ({
      ...s,
      index: (s.index - 1 + images.length) % images.length,
    }));
  };

  return (
    <section className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 py-10 sm:py-14 md:py-20 lg:py-24">
      <div className="max-w-6xl mx-auto">

        {/* Heading */}
        <h1 className="section-heading mb-3 sm:mb-4">Gallery</h1>
        <p className="section-sub mb-8 sm:mb-12">
          A selection of moments we've captured. Elegance, precision, and passion.
        </p>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[220px] md:auto-rows-[260px] gap-3 md:gap-4">
          {(loading ? Array.from({ length: 8 }) : images).map((item, i) => {
            const layout =
              i === 0
                ? "md:col-span-2 md:row-span-2"
                : i === 3
                ? "md:row-span-2"
                : i === 5
                ? "md:col-span-2"
                : "";

            return (
              <div
                key={i}
                className={`relative overflow-hidden rounded-2xl ${layout}`}
              >
                {loading ? (
                  <Skeleton />
                ) : (
                  <img
                    src={item.src}
                    alt={item.alt}
                    
                    decoding="async"
                    onClick={() => openLightbox(i)}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110 cursor-zoom-in"
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="mt-12 sm:mt-14 md:mt-16 flex flex-wrap gap-4">
          <Link to="/photography" className="btn-ghost">
            ← Photography
          </Link>
          <Link to="/" className="btn-ghost">
            ← Home
          </Link>
        </div>
      </div>

      {/* 🔥 LIGHTBOX */}
      {lightbox.open && images.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Prev */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-4 md:left-8 text-white text-4xl font-light opacity-70 hover:opacity-100"
          >
            ‹
          </button>

          {/* Image */}
          <img
            src={images[lightbox.index].src}
            alt={images[lightbox.index].alt}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-4 md:right-8 text-white text-4xl font-light opacity-70 hover:opacity-100"
          >
            ›
          </button>

          {/* Close */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white text-2xl opacity-70 hover:opacity-100"
          >
            ✕
          </button>
        </div>
      )}
    </section>
  );
}
